import { getNearestScrollableParent } from '@fabric-app/utils/element';
import { Extension } from '@tiptap/core';
import { PluginKey, Plugin } from '@tiptap/pm/state';
import { DecorationSet, Decoration } from '@tiptap/pm/view';

const SearchPluginKey = new PluginKey("search");
function scrollToMatch(view, matchIndex) {
  const element = view.dom.querySelector(`[data-search-match][data-search-match-index="${matchIndex}"]`);
  if (element) {
    const scrollableParent = getNearestScrollableParent(element);
    if (scrollableParent) {
      const elRect = element.getBoundingClientRect();
      const parentRect = scrollableParent.getBoundingClientRect();
      const isInView = elRect.top >= parentRect.top && elRect.bottom <= parentRect.bottom;
      if (!isInView) {
        const elementCenter = elRect.top + elRect.height / 2;
        const parentCenter = parentRect.top + parentRect.height / 2;
        const offset = elementCenter - parentCenter;
        scrollableParent.scrollBy({
          top: offset,
          behavior: "smooth"
        });
      }
    }
  }
}
const SearchHandler = Extension.create({
  name: "searchHandler",
  addCommands() {
    return {
      setSearch: (regexp) => ({ chain }) => {
        return chain().command(({ tr }) => {
          tr.setMeta(SearchPluginKey, {
            search: regexp,
            currentIndex: regexp ? 0 : null
          });
          return true;
        }).run();
      },
      goToMatch: (match) => ({ chain }) => {
        return chain().command(({ tr }) => {
          tr.setMeta(SearchPluginKey, {
            currentIndex: match
          });
          return true;
        }).run();
      },
      setOnSearchStateChange: (onSearchStateChange) => ({ chain }) => {
        return chain().command(({ tr }) => {
          tr.setMeta(SearchPluginKey, {
            onSearchStateChange
          });
          return true;
        }).run();
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: SearchPluginKey,
        state: {
          init() {
            return {
              search: null,
              totalMatches: 0,
              currentIndex: null,
              decorations: DecorationSet.empty
            };
          },
          apply(tr, searchState, _oldState, state) {
            const set = tr.getMeta(SearchPluginKey);
            const onSearchStateChange = set?.onSearchStateChange !== void 0 ? set.onSearchStateChange : searchState.onSearchStateChange;
            const newSearch = set?.search !== void 0 ? set.search : searchState.search;
            const newCurrentIndex = set?.currentIndex !== void 0 ? set.currentIndex : searchState.currentIndex;
            if (!set && !tr.docChanged && newCurrentIndex === searchState.currentIndex) {
              return searchState;
            }
            let decorations = searchState.decorations;
            let totalMatches = searchState.totalMatches;
            if (newSearch !== searchState.search || tr.docChanged || decorations === DecorationSet.empty) {
              if (newSearch === null) {
                decorations = DecorationSet.empty;
                totalMatches = 0;
              } else {
                const textNodes = [];
                let wasText = false;
                state.doc.descendants((node, pos) => {
                  if (!node.isText) {
                    wasText = false;
                    return;
                  }
                  if (!wasText) {
                    textNodes.push({
                      text: node.text ?? "",
                      position: pos
                    });
                  } else {
                    textNodes[textNodes.length - 1].text += node.text ?? "";
                  }
                  wasText = true;
                });
                const decorationList = [];
                let matchIndex = 0;
                totalMatches = 0;
                for (const textNode of textNodes) {
                  const { text, position } = textNode;
                  if (!newSearch) continue;
                  const matches = Array.from(text.matchAll(newSearch));
                  totalMatches += matches.length;
                  for (const match of matches) {
                    if (match.index === void 0 || match.index < 0) continue;
                    const isActiveMatch = newCurrentIndex === matchIndex;
                    const decoration = Decoration.inline(position + match.index, position + match.index + match[0].length, {
                      ["data-search-match"]: "true",
                      ["data-search-match-index"]: matchIndex.toString(),
                      ...isActiveMatch ? {
                        ["data-search-match-active"]: "true"
                      } : {}
                    }, {
                      matchIndex: matchIndex.toString()
                    });
                    decorationList.push(decoration);
                    matchIndex++;
                  }
                }
                decorations = DecorationSet.create(tr.doc, decorationList);
              }
            } else if (newCurrentIndex !== searchState.currentIndex && newCurrentIndex !== null) {
              const oldDecorations = searchState.decorations.find();
              const newDecorationList = oldDecorations.map((decoration) => {
                const matchIndex = parseInt(decoration.spec["matchIndex"], 10);
                const isActiveMatch = matchIndex === newCurrentIndex;
                return Decoration.inline(decoration.from, decoration.to, {
                  ["data-search-match"]: "true",
                  ["data-search-match-index"]: matchIndex.toString(),
                  ...isActiveMatch ? {
                    ["data-search-match-active"]: "true"
                  } : {}
                }, {
                  matchIndex: matchIndex.toString()
                });
              });
              decorations = DecorationSet.create(tr.doc, newDecorationList);
            }
            const newState = {
              search: newSearch,
              totalMatches,
              currentIndex: newCurrentIndex,
              decorations,
              onSearchStateChange
            };
            if (onSearchStateChange && (newSearch !== searchState.search || newCurrentIndex !== searchState.currentIndex || totalMatches !== searchState.totalMatches)) {
              const { onSearchStateChange: _, ...stateWithoutFunction } = newState;
              onSearchStateChange(stateWithoutFunction);
            }
            return newState;
          }
        },
        props: {
          decorations(state) {
            return this.getState(state)?.search ? this.getState(state)?.decorations ?? DecorationSet.empty : DecorationSet.empty;
          }
        },
        view(editorView) {
          const pluginKey = SearchPluginKey;
          return {
            update(_view, prevState) {
              const currentState = pluginKey.getState(editorView.state);
              const prevPluginState = pluginKey.getState(prevState);
              if (currentState && typeof currentState.currentIndex === "number" && (currentState.currentIndex !== prevPluginState?.currentIndex || currentState.search !== prevPluginState?.search)) {
                scrollToMatch(editorView, currentState.currentIndex);
              }
            }
          };
        }
      })
    ];
  }
});

export { SearchHandler };
//# sourceMappingURL=SearchHandler.js.map
